"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Send,
  Loader2,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  Bot,
  User as UserIcon,
  Phone,
  Building2,
  CheckCheck,
  Sparkles,
  Smartphone,
  ShieldCheck,
  ArrowDown,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
  Eye,
  FileCheck,
  UploadCloud,
  File,
  Check,
} from "lucide-react";
import { Lead, WhatsAppMessage, SmsMessage } from "@/lib/db";

interface WhatsAppChatDrawerProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onMessageSent?: () => void;
}

const WA_QUICK_RESPONSES = [
  {
    label: "👋 Intro Greeting",
    text: "Hi! Thanks for contacting Johnson Softwares. I'm reviewing your inquiry—how can we best assist your team today?",
  },
  {
    label: "🏢 All Demos (Master Catalog)",
    text: "Here are all 8 of our live interactive industry demos: https://johnsonsoftwares.com/demos\n\n1. Real Estate: https://johnsonsoftwares.com/demos/real-estate\n2. Restaurant: https://johnsonsoftwares.com/demos/restaurant\n3. Gym: https://johnsonsoftwares.com/demos/gym\n4. Dental: https://johnsonsoftwares.com/demos/dental\n5. Insurance: https://johnsonsoftwares.com/demos/insurance\n6. Telecalling: https://johnsonsoftwares.com/demos/telecalling\n7. Hiring: https://johnsonsoftwares.com/demos/hiring\n8. Legal: https://johnsonsoftwares.com/demos/legal\n\nWhich vertical would you like to deep-dive into?",
  },
  {
    label: "🏡 Real Estate Demo",
    text: "Here is our live Real Estate demo suite:\n📲 Buyer WhatsApp Portal: https://johnsonsoftwares.com/demos/real-estate\n📊 Broker Admin CRM: https://johnsonsoftwares.com/demos/real-estate/crm\n\nSub-15s speed-to-lead, automated floor plans, and zero per-seat SaaS tax!",
  },
  {
    label: "🍽️ Restaurant Demo",
    text: "Explore our Restaurant Table Booking Hub:\n📲 Guest Reservation: https://johnsonsoftwares.com/demos/restaurant\n📊 Floor Manager CRM: https://johnsonsoftwares.com/demos/restaurant/crm\n\n0% cover commissions and direct WhatsApp reservations!",
  },
  {
    label: "🏋️ Gym Demo",
    text: "Test our IronPulse Fitness & Gym Club CRM:\n📲 Member VIP Pass: https://johnsonsoftwares.com/demos/gym\n📊 Gym Owner CRM: https://johnsonsoftwares.com/demos/gym/crm\n\nAutomated expiry reminders cut member drop-offs by 40%!",
  },
  {
    label: "🦷 Dental Demo",
    text: "Check out our Dental & Clinic Patient Portal:\n📲 Patient 3D Scan: https://johnsonsoftwares.com/demos/dental\n📊 Doctor Chair CRM: https://johnsonsoftwares.com/demos/dental/crm\n\nAutomated recall cadences cut no-shows by 35%!",
  },
  {
    label: "📞 Telecalling Demo",
    text: "Test our High-Velocity Telecalling & Dialer CRM:\n📲 1-Click Dialing Queue: https://johnsonsoftwares.com/demos/telecalling\n📊 Call Center Admin CRM: https://johnsonsoftwares.com/demos/telecalling/crm\n\nBoosts rep dial volume by 60% with zero dialer fatigue!",
  },
  {
    label: "🛡️ Insurance Demo",
    text: "Explore our Insurance Advisory CRM:\n📲 Premium Calculator: https://johnsonsoftwares.com/demos/insurance\n📊 Policy Vault CRM: https://johnsonsoftwares.com/demos/insurance/crm\n\nAutomated WhatsApp renewal notices and paperless KYC!",
  },
  {
    label: "💼 Hiring Demo",
    text: "Test our Staffing & Executive Recruitment CRM:\n📲 Candidate Evaluation: https://johnsonsoftwares.com/demos/hiring\n📊 Headhunter CRM: https://johnsonsoftwares.com/demos/hiring/crm",
  },
  {
    label: "⚖️ Legal Demo",
    text: "Test our Law Firms & Legal Practice Chambers CRM:\n📲 Client Intake Portal: https://johnsonsoftwares.com/demos/legal\n📊 Chambers Practice CRM: https://johnsonsoftwares.com/demos/legal/crm\n\nBCI Rule 36 compliant with live CNR case tracking!",
  },
  {
    label: "⚡ SaaS Tier Pricing",
    text: "Here are our official SaaS CRM plans (Zero per-seat monthly tax):\n\n1. Starter: ₹2,499/mo (2 Users, 250 leads/mo)\n2. Growth: ₹6,999/mo (5 Users, 1,000 leads/mo + WhatsApp Speed-to-Lead Bot)\n3. Professional: ₹14,999/mo (10 Users, 2,500 leads/mo + Multi-Agent Team Bot)\n4. Enterprise: ₹29,999/mo (Unlimited users on dedicated private VPS)\n\nCalculate plan ROI & explore: https://johnsonsoftwares.com/pricing",
  },
  {
    label: "🤖 WhatsApp Chatbot Setup",
    text: "Here are our WhatsApp Speed-to-Lead Chatbot solutions:\n\n• Option A: Included with Growth Plan (₹6,999/mo) with automated lead capture & qualification.\n• Option B: Standalone Turnkey Deployment: ₹14,999 one-time setup (Meta approval, custom prompts & brochure dispatch) + ₹2,999/mo container retainer.\n\nOfficial Meta Cloud API conversation fees billed at direct actuals (₹0 markup).",
  },
  {
    label: "🧾 Tax & Billing Notice",
    text: "Tax & Invoicing Details:\n\n• Entity: Johnson Softwares (Micro-Enterprise)\n• Registration: MSME Udyam UDYAM-KR-03-0149216 • PAN: AALCJ1234F\n• Statutory Status: Issued as Bill of Supply with 0% GST under Section 22 threshold exemption (zero added tax).\n• Payment Modes: UPI, Corporate Card, NEFT/RTGS/SWIFT wire.\n\nReply with your legal name to receive an instant commercial invoice.",
  },
  {
    label: "📜 Legal Agreements & SLA",
    text: "All Johnson Softwares subscriptions are backed by transparent legal agreements:\n\n• Terms of Service: https://johnsonsoftwares.com/terms\n• 99.9% Uptime SLA: https://johnsonsoftwares.com/sla\n• Refund Policy: https://johnsonsoftwares.com/refund\n• Data Processing Addendum (DPA): https://johnsonsoftwares.com/dpa\n• WhatsApp Anti-Spam AUP: https://johnsonsoftwares.com/aup\n\nAll subscriptions include paperless, immutable click-wrap consent logs.",
  },
  {
    label: "📅 Discovery Call",
    text: "Would you have 10-15 minutes tomorrow for a brief walkthrough of how our dedicated pipeline system works for your industry?",
  },
];

const SMS_QUICK_RESPONSES = [
  {
    label: "🏡 Property Tour",
    text: "Hi! Thanks for your inquiry. When would you like to schedule an exclusive private walkthrough of this listing?",
  },
  {
    label: "📑 Full Brochure",
    text: "Here are the complete specs, high-res gallery, and floor plans: https://johnsonsoftwares.com/demos/real-estate",
  },
  {
    label: "💰 Price & HOA",
    text: "The property is available with verified title and HOA disclosure. Would you like me to text you the CMA market valuation report?",
  },
  {
    label: "📞 2-Min Call",
    text: "Are you free for a quick 2-minute call to discuss your buying timeline and pre-approval status?",
  },
];

export function WhatsAppChatDrawer({ lead, isOpen, onClose, onMessageSent }: WhatsAppChatDrawerProps) {
  const [channel, setChannel] = useState<"whatsapp" | "sms">("whatsapp");
  const [waMessages, setWaMessages] = useState<WhatsAppMessage[]>([]);
  const [smsMessages, setSmsMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [outside24hFallbackUrl, setOutside24hFallbackUrl] = useState<string | null>(null);
  const [botPaused, setBotPaused] = useState<boolean>(lead?.bot_paused === 1);
  const [togglingBot, setTogglingBot] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef<boolean>(true);
  const [showScrollBottom, setShowScrollBottom] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Document & Attachment state
  const [pendingAttachment, setPendingAttachment] = useState<{
    type: "document" | "image" | "audio" | "video";
    url: string;
    filename: string;
    size?: number;
    mimeType?: string;
  } | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  const [showAttachMenu, setShowAttachMenu] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (lead) {
      setBotPaused(lead.bot_paused === 1);
    }
  }, [lead?.id, lead?.bot_paused]);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior,
      });
      isNearBottomRef.current = true;
      setShowScrollBottom(false);
    }
  };

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNear = distanceFromBottom < 90;
    isNearBottomRef.current = isNear;
    setShowScrollBottom(!isNear);
  };

  const handleToggleBot = async (targetPaused: boolean) => {
    if (!lead) return;
    setTogglingBot(true);
    try {
      const res = await fetch("/api/crm/whatsapp/bot-toggle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: lead.id, paused: targetPaused }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBotPaused(targetPaused);
        if (onMessageSent) onMessageSent();
      } else {
        setError(data.error || "Failed to update bot status.");
      }
    } catch {
      setError("Network error updating bot status.");
    } finally {
      setTogglingBot(false);
    }
  };

  const fetchChannelMessages = async (isBackground = false) => {
    if (!lead) return;
    if (!isBackground) setLoading(true);
    setError("");

    try {
      if (channel === "whatsapp") {
        const res = await fetch(`/api/crm/whatsapp/messages?lead_id=${lead.id}&phone=${encodeURIComponent(lead.phone || "")}`);
        if (res.ok) {
          const data = await res.json();
          const newMsgs: WhatsAppMessage[] = data.messages || [];
          setWaMessages((prev) => {
            if (
              prev.length === newMsgs.length &&
              prev.length > 0 &&
              prev[prev.length - 1]?.id === newMsgs[newMsgs.length - 1]?.id
            ) {
              return prev;
            }
            if (!isBackground || isNearBottomRef.current) {
              setTimeout(() => scrollToBottom(!isBackground ? "auto" : "smooth"), 60);
            } else if (newMsgs.length > prev.length) {
              setShowScrollBottom(true);
            }
            return newMsgs;
          });
        } else if (!isBackground) {
          setError("Failed to fetch WhatsApp conversation.");
        }
      } else {
        const res = await fetch(`/api/crm/sms/messages?lead_id=${lead.id}&phone=${encodeURIComponent(lead.phone || "")}`);
        if (res.ok) {
          const data = await res.json();
          const newMsgs: SmsMessage[] = data.messages || [];
          setSmsMessages((prev) => {
            if (
              prev.length === newMsgs.length &&
              prev.length > 0 &&
              prev[prev.length - 1]?.id === newMsgs[newMsgs.length - 1]?.id
            ) {
              return prev;
            }
            if (!isBackground || isNearBottomRef.current) {
              setTimeout(() => scrollToBottom(!isBackground ? "auto" : "smooth"), 60);
            } else if (newMsgs.length > prev.length) {
              setShowScrollBottom(true);
            }
            return newMsgs;
          });
        } else if (!isBackground) {
          setError("Failed to fetch SMS messages.");
        }
      }
    } catch {
      if (!isBackground) setError("Network error loading messages.");
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && lead) {
      isNearBottomRef.current = true;
      setShowScrollBottom(false);
      fetchChannelMessages();
      const interval = setInterval(() => {
        fetchChannelMessages(true);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, lead?.id, channel]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 300);
    }
  }, [isOpen, channel]);

  // Upload handler for document/photo from computer
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAttachment(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/crm/whatsapp/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setPendingAttachment({
          type: data.mediaType || "document",
          url: data.url,
          filename: data.filename || file.name,
          size: data.size || file.size,
          mimeType: data.mimeType || file.type,
        });
        setShowAttachMenu(false);
      } else {
        setError(data.error || "Failed to upload file attachment.");
      }
    } catch {
      setError("Network timeout while uploading document.");
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 1-Click preset documents handler
  const handleAttachPreset = (type: "proposal" | "invoice" | "brochure") => {
    if (!lead) return;
    const cleanLeadName = (lead.name || "Client").replace(/[^a-zA-Z0-9]/g, "_");

    if (type === "proposal") {
      setPendingAttachment({
        type: "document",
        url: `/api/crm/proposals/download?leadId=${lead.id}&tier=1`,
        filename: `${cleanLeadName}_Proposal_SOW.pdf`,
        mimeType: "application/pdf",
      });
    } else if (type === "invoice") {
      setPendingAttachment({
        type: "document",
        url: `/api/crm/proposals/download?leadId=${lead.id}&tier=2`,
        filename: `${cleanLeadName}_Proforma_Invoice.pdf`,
        mimeType: "application/pdf",
      });
    } else if (type === "brochure") {
      setPendingAttachment({
        type: "document",
        url: `/api/crm/proposals/download?leadId=${lead.id}&tier=3`,
        filename: `Johnson_Softwares_CRM_Brochure.pdf`,
        mimeType: "application/pdf",
      });
    }
    setShowAttachMenu(false);
  };

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend !== undefined ? textToSend : inputMessage).trim();
    if ((!text && !pendingAttachment) || !lead) return;

    setSending(true);
    setError("");

    try {
      if (channel === "whatsapp") {
        const payload: any = {
          lead_id: lead.id,
          phone: lead.phone,
          message: text,
        };

        if (pendingAttachment) {
          payload.media_type = pendingAttachment.type;
          payload.media_url = pendingAttachment.url;
          payload.media_filename = pendingAttachment.filename;
          payload.media_mime_type = pendingAttachment.mimeType;
          payload.media_file_size = pendingAttachment.size;
        }

        const res = await fetch("/api/crm/whatsapp/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (res.ok && data.success) {
          setInputMessage("");
          setPendingAttachment(null);
          setWaMessages((prev) => [...prev, data.message]);
          setBotPaused(true);
          if (data.outside24hWindow && data.whatsappUrl) {
            setOutside24hFallbackUrl(data.whatsappUrl);
          } else {
            setOutside24hFallbackUrl(null);
          }
          setTimeout(() => scrollToBottom("smooth"), 50);
          if (onMessageSent) onMessageSent();
        } else {
          setError(data.error || "Failed to dispatch WhatsApp message.");
        }
      } else {
        // Direct SMS
        const res = await fetch("/api/crm/sms/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lead_id: lead.id,
            phone: lead.phone,
            message: text,
          }),
        });

        const data = await res.json();
        if (res.ok && data.success) {
          setInputMessage("");
          setSmsMessages((prev) => [...prev, data.message]);
          setBotPaused(true);
          setTimeout(() => scrollToBottom("smooth"), 50);
          if (onMessageSent) onMessageSent();
        } else {
          setError(data.error || "Failed to dispatch SMS text.");
        }
      }
    } catch {
      setError("Network timeout while dispatching message.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen || !lead) return null;

  const cleanPhone = (lead.phone || "").replace(/[^0-9]/g, "");
  const waExternalUrl = cleanPhone ? `https://wa.me/${cleanPhone}` : null;
  const activeQuickResponses = channel === "whatsapp" ? WA_QUICK_RESPONSES : SMS_QUICK_RESPONSES;
  const currentMessages = channel === "whatsapp" ? waMessages : smsMessages;

  const charCount = inputMessage.length;
  const smsSegments = Math.ceil(charCount / 160) || 1;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm transition-opacity duration-300">
      {/* Click outside to close backdrop */}
      <div className="flex-1" onClick={onClose} />

      {/* Slide-over Drawer Panel */}
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
        
        {/* Top Navigation & Lead Header */}
        <div className="p-4 bg-slate-900 text-white border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`relative w-10 h-10 rounded-full border flex items-center justify-center font-bold shrink-0 ${
              channel === "whatsapp" 
                ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400" 
                : "bg-blue-500/20 border-blue-500/30 text-blue-400"
            }`}>
              {lead.name.charAt(0).toUpperCase()}
              <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-slate-900 ${
                channel === "whatsapp" ? "bg-emerald-500" : "bg-blue-500"
              }`} />
            </div>

            <div className="min-w-0">
              <h3 className="font-bold text-sm text-white truncate flex items-center gap-2">
                {lead.name}
                <span className={`text-[10px] font-semibold px-1.5 py-0.2 rounded border ${
                  channel === "whatsapp" 
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" 
                    : "bg-blue-500/20 text-blue-300 border-blue-500/30"
                }`}>
                  {channel === "whatsapp" ? "WhatsApp Live" : "Direct SMS"}
                </span>
              </h3>
              <p className="text-xs text-slate-400 truncate flex items-center gap-1.5 mt-0.5">
                <Phone className={`w-3 h-3 shrink-0 ${channel === "whatsapp" ? "text-emerald-400" : "text-blue-400"}`} />
                <span>{lead.phone || "No phone registered"}</span>
                {lead.company_name && (
                  <>
                    <span>•</span>
                    <span className="truncate">{lead.company_name}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {channel === "whatsapp" && waExternalUrl && (
              <a
                href={waExternalUrl}
                target="_blank"
                rel="noreferrer"
                title="Open in WhatsApp Web"
                className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}

            <button
              onClick={() => fetchChannelMessages()}
              disabled={loading}
              title="Refresh conversation"
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-cyan-400" : ""}`} />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Multi-Channel Switcher Tabs */}
        <div className="grid grid-cols-2 p-1 bg-slate-950 border-b border-slate-800 text-xs font-bold shrink-0">
          <button
            type="button"
            onClick={() => setChannel("whatsapp")}
            className={`py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-all cursor-pointer ${
              channel === "whatsapp"
                ? "bg-emerald-600 text-white shadow-md shadow-emerald-950/50"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>WhatsApp Cloud</span>
          </button>
          <button
            type="button"
            onClick={() => setChannel("sms")}
            className={`py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-all cursor-pointer ${
              channel === "sms"
                ? "bg-blue-600 text-white shadow-md shadow-blue-950/50"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Direct SMS (US / Global)</span>
          </button>
        </div>

        {/* Human Takeover & AI Bot Status Bar */}
        {channel === "whatsapp" && (
          <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs shrink-0">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${botPaused ? "bg-amber-400" : "bg-emerald-500 animate-pulse"}`} />
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {botPaused ? "Human Takeover Active" : "AI Speed-to-Lead Bot Active"}
              </span>
              <span className="text-[10px] text-slate-400">
                ({botPaused ? "Bot paused" : "Auto-qualifying"})
              </span>
            </div>

            <button
              onClick={() => handleToggleBot(!botPaused)}
              disabled={togglingBot}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                botPaused
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-600 dark:text-amber-400 border border-amber-500/30"
              }`}
            >
              {togglingBot ? "Updating..." : botPaused ? "Resume Bot Auto-Replies" : "Pause Bot (Take Over)"}
            </button>
          </div>
        )}

        {/* Conversation Message List Container */}
        <div className="relative flex-1 overflow-hidden flex flex-col">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-slate-50 dark:bg-slate-950/70"
          >
            {loading && currentMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="text-xs">
                  {channel === "whatsapp" ? "Loading WhatsApp thread..." : "Loading SMS text history..."}
                </span>
              </div>
            ) : currentMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 text-center p-6 space-y-3">
                <div className={`w-12 h-12 rounded-full border flex items-center justify-center ${
                  channel === "whatsapp" 
                    ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" 
                    : "bg-blue-500/10 border-blue-500/20 text-blue-500"
                }`}>
                  {channel === "whatsapp" ? <MessageSquare className="w-6 h-6" /> : <Smartphone className="w-6 h-6" />}
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-700 dark:text-slate-300">
                    {channel === "whatsapp" ? "No WhatsApp message history yet" : "No Direct SMS text history yet"}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 max-w-xs">
                    {channel === "whatsapp" 
                      ? "Dispatch instant speed-to-lead brochures, receive customer documents, and chat live via Meta Cloud API."
                      : "Send direct SMS text messages to US & International cell phones via the Twilio SMS gateway."}
                  </p>
                </div>
              </div>
            ) : (
              currentMessages.map((msg, idx) => {
                const isInbound = msg.direction === "inbound";
                const isBot = msg.sender_type === "bot";
                const isSimulated = "status" in msg && msg.status === "simulated";

                // Media detection (WhatsApp specific)
                const waMsg = "media_url" in msg ? (msg as WhatsAppMessage) : null;
                const hasMedia = Boolean(waMsg && (waMsg.media_url || waMsg.media_type || waMsg.media_id));
                const mediaType = (waMsg?.media_type || "").toLowerCase();
                const mediaUrl = waMsg?.media_url || "";
                const mimeType = (waMsg?.media_mime_type || "").toLowerCase();
                const filename = waMsg?.media_filename || "";
                const mediaFileSize = waMsg?.media_file_size || null;

                const isImage = 
                  mediaType === "image" || 
                  mimeType.startsWith("image/") || 
                  /\.(jpg|jpeg|png|webp|gif)($|\?)/i.test(mediaUrl) ||
                  (/\.(jpg|jpeg|png|webp|gif)$/i.test(filename));

                const isDoc = 
                  mediaType === "document" || 
                  Boolean(filename) || 
                  mimeType.includes("pdf") || 
                  mimeType.includes("document") || 
                  mimeType.includes("sheet") || 
                  /\.(pdf|doc|docx|xls|xlsx|txt)($|\?)/i.test(mediaUrl) ||
                  (/\.(pdf|doc|docx|xls|xlsx|txt)$/i.test(filename));

                const isAudio = 
                  mediaType === "audio" || 
                  mediaType === "voice" || 
                  mimeType.startsWith("audio/") || 
                  /\.(mp3|ogg|wav|opus|m4a)($|\?)/i.test(mediaUrl);

                const isVideo = 
                  mediaType === "video" || 
                  mimeType.startsWith("video/") || 
                  /\.(mp4|webm|mov)($|\?)/i.test(mediaUrl);

                // Clean display text without redundant bracket prefixes when media card renders
                let cleanDisplayText = (msg.message_text || "").trim();
                if (/\[UNSUPPORTED[^\]]*\]/i.test(cleanDisplayText)) {
                  cleanDisplayText = "🏷️ [WhatsApp Sticker / View-Once media received]";
                }

                if (hasMedia) {
                  if (/^📷\s*\[Photo[^\]]*\]/i.test(cleanDisplayText)) {
                    cleanDisplayText = cleanDisplayText.replace(/^📷\s*\[Photo[^\]]*\]\s*/i, "").trim();
                  } else if (/^📄\s*\[Document[^\]]*\]/i.test(cleanDisplayText)) {
                    cleanDisplayText = cleanDisplayText.replace(/^📄\s*\[Document[^\]]*\]\s*/i, "").trim();
                  } else if (/^[🎤🎵]\s*\[[^\]]*\]/i.test(cleanDisplayText)) {
                    cleanDisplayText = cleanDisplayText.replace(/^[🎤🎵]\s*\[[^\]]*\]\s*/i, "").trim();
                  } else if (/^🎥\s*\[Video[^\]]*\]/i.test(cleanDisplayText)) {
                    cleanDisplayText = cleanDisplayText.replace(/^🎥\s*\[Video[^\]]*\]\s*/i, "").trim();
                  }
                }

                if (!cleanDisplayText && !hasMedia) {
                  cleanDisplayText = isInbound ? "💬 [Incoming response / reply received]" : "💬 [Message dispatched]";
                }

                return (
                  <div
                    key={msg.id || idx}
                    className={`flex flex-col ${isInbound ? "items-start" : "items-end"}`}
                  >
                    {/* Sender Badge */}
                    <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1 px-1">
                      {isInbound ? (
                        <span className="font-medium text-slate-600 dark:text-slate-300">
                          {msg.sender_name || lead.name}
                        </span>
                      ) : isBot ? (
                        <span className="flex items-center gap-1 font-semibold text-blue-600 dark:text-blue-400">
                          <Bot className="w-3 h-3" /> Autonomous Bot
                        </span>
                      ) : (
                        <span className={`flex items-center gap-1 font-semibold ${
                          channel === "whatsapp" ? "text-emerald-600 dark:text-emerald-400" : "text-blue-600 dark:text-blue-400"
                        }`}>
                          <UserIcon className="w-3 h-3" /> {msg.sender_name || "Sales Rep"}
                        </span>
                      )}
                      <span>•</span>
                      <span>{msg.created_at || "Just now"}</span>
                      {isSimulated && (
                        <span className="px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-500 font-bold text-[9px] border border-amber-500/20">
                          Sandbox
                        </span>
                      )}
                    </div>

                    {/* Chat Bubble */}
                    <div
                      className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-xs shadow-sm leading-relaxed ${
                        isInbound
                          ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-tl-sm"
                          : isBot
                          ? "bg-blue-600 text-white rounded-tr-sm"
                          : channel === "whatsapp"
                          ? "bg-emerald-600 text-white rounded-tr-sm"
                          : "bg-blue-600 text-white rounded-tr-sm"
                      }`}
                    >
                      {/* Customer Attachment Banner */}
                      {isInbound && hasMedia && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                          <FileCheck className="w-3 h-3 text-emerald-500" />
                          <span>Customer Attachment Received</span>
                        </div>
                      )}

                      {/* Image Preview */}
                      {isImage && (
                        <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-black/5 dark:bg-black/20">
                          <a
                            href={mediaUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group relative cursor-pointer"
                          >
                            <img
                              src={mediaUrl}
                              alt={filename || "WhatsApp Image"}
                              className="max-h-72 w-full object-contain rounded-xl transition-transform duration-200 group-hover:scale-[1.01]"
                              loading="lazy"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.onerror = null;
                                target.src = "/brand/app-icon.svg";
                              }}
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white font-medium text-xs backdrop-blur-xs">
                              <Eye className="w-4 h-4" /> Open Full Image
                            </div>
                          </a>
                        </div>
                      )}

                      {/* Document / PDF Card */}
                      {isDoc && (
                        <div className={`mb-2 p-3 rounded-xl border flex items-center justify-between gap-3 shadow-xs ${
                          isInbound
                            ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                            : "bg-white/10 border-white/20 text-white"
                        }`}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                              isInbound ? "bg-rose-500/10 text-rose-500 border border-rose-500/20" : "bg-white/20 text-white"
                            }`}>
                              <FileText className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <p className={`font-bold text-xs truncate ${isInbound ? "text-slate-800 dark:text-slate-100" : "text-white"}`}>
                                {filename || "Document.pdf"}
                              </p>
                              <p className={`text-[10px] ${isInbound ? "text-slate-400" : "text-emerald-100"}`}>
                                {mediaFileSize ? `${Math.round(mediaFileSize / 1024)} KB • ` : ""}
                                {isInbound ? "Customer Document" : "Sent Document"}
                              </p>
                            </div>
                          </div>
                          {mediaUrl && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              <a
                                href={mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={filename || "document"}
                                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                                  isInbound
                                    ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                                    : "bg-white text-emerald-800 hover:bg-emerald-50"
                                }`}
                                title="Download or View Document"
                              >
                                <Download className="w-3.5 h-3.5" />
                                <span>Download</span>
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Audio / Voice Player */}
                      {isAudio && (
                        <div className="mb-2">
                          <audio controls className="w-full h-8" src={mediaUrl || undefined}>
                            Your browser does not support audio element.
                          </audio>
                        </div>
                      )}

                      {/* Video Player */}
                      {isVideo && (
                        <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                          <video controls className="w-full max-h-72" src={mediaUrl || undefined} />
                        </div>
                      )}

                      {/* Accompanying Message Text */}
                      {cleanDisplayText && (
                        <div className="whitespace-pre-wrap">{cleanDisplayText}</div>
                      )}

                      {!isInbound && (
                        <div className="flex justify-end items-center gap-1 mt-1 text-[9px] opacity-80">
                          <CheckCheck className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Floating Scroll to Bottom Button */}
          {showScrollBottom && (
            <button
              type="button"
              onClick={() => scrollToBottom("smooth")}
              className="absolute bottom-3 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/90 hover:bg-slate-900 text-white dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 text-xs font-semibold shadow-lg hover:scale-105 active:scale-95 transition-all cursor-pointer backdrop-blur-sm border border-slate-700/40 dark:border-slate-300/40 animate-in fade-in slide-in-from-bottom-2 duration-200"
            >
              <ArrowDown className="w-3.5 h-3.5" />
              <span>Latest messages</span>
            </button>
          )}
        </div>

        {/* Error Notification Banner */}
        {error && (
          <div className="px-4 py-2 bg-rose-500/10 border-t border-rose-500/20 text-rose-500 text-xs font-medium flex items-center justify-between shrink-0">
            <span>{error}</span>
            <button onClick={() => setError("")} className="hover:text-rose-700">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Cold Contact 24h Window Fallback */}
        {outside24hFallbackUrl && (
          <div className="px-4 py-2.5 bg-amber-500/10 border-t border-amber-500/20 text-xs flex items-center justify-between gap-3 shrink-0">
            <span className="text-amber-400 font-medium text-[11px]">
              ⚠️ Outside Meta's 24h window (cold contact). Send directly:
            </span>
            <div className="flex items-center gap-2">
              <a
                href={outside24hFallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOutside24hFallbackUrl(null)}
                className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] flex items-center gap-1.5 transition-all shadow-sm"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Open in WhatsApp ➔</span>
              </a>
              <button
                type="button"
                onClick={() => setOutside24hFallbackUrl(null)}
                className="text-slate-400 hover:text-white text-xs px-1 cursor-pointer"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* 1-Click Quick Response Chips */}
        <div className="px-3 py-2 bg-slate-100/80 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center justify-between mb-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            <div className="flex items-center gap-1">
              <Sparkles className={`w-3 h-3 ${channel === "whatsapp" ? "text-emerald-500" : "text-blue-500"}`} />
              <span>{channel === "whatsapp" ? "WhatsApp Templates" : "US Real Estate & SMS Templates"}</span>
            </div>
            {channel === "sms" && (
              <span className="text-[10px] font-mono text-slate-400">
                {charCount}/160 chars ({smsSegments} SMS)
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            {activeQuickResponses.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => setInputMessage(chip.text)}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 whitespace-nowrap transition-colors shadow-xs cursor-pointer ${
                  channel === "whatsapp"
                    ? "hover:border-emerald-500 hover:text-emerald-500 dark:hover:text-emerald-400"
                    : "hover:border-blue-500 hover:text-blue-500 dark:hover:text-blue-400"
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message Composer Footer */}
        <div className="p-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0">
          
          {/* Pending Attachment Banner above textarea */}
          {pendingAttachment && (
            <div className="mb-2 p-2 rounded-xl bg-emerald-500/10 dark:bg-emerald-950/30 border border-emerald-500/20 flex items-center justify-between gap-2 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  {pendingAttachment.type === "image" ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 truncate">
                    {pendingAttachment.filename}
                  </p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                    Ready to send {pendingAttachment.size ? `(${Math.round(pendingAttachment.size / 1024)} KB)` : ""}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPendingAttachment(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer"
                title="Remove attachment"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="relative flex items-end gap-2">
            {/* Attachment Button & Dropdown (WhatsApp channel) */}
            {channel === "whatsapp" && (
              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() => setShowAttachMenu(!showAttachMenu)}
                  disabled={uploadingAttachment}
                  className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors flex items-center justify-center gap-1 shrink-0 cursor-pointer"
                  title="Attach document or image"
                >
                  {uploadingAttachment ? (
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                  ) : (
                    <Paperclip className="w-4 h-4" />
                  )}
                </button>

                {/* Attachment Popup Menu */}
                {showAttachMenu && (
                  <div className="absolute bottom-12 left-0 z-30 w-72 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl p-2 animate-in fade-in slide-in-from-bottom-2 duration-150">
                    <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Attach to WhatsApp
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors cursor-pointer text-left"
                    >
                      <UploadCloud className="w-4 h-4 text-emerald-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate">Upload Document / Photo</p>
                        <p className="text-[10px] font-normal text-slate-400">PDF, JPG, PNG, DOC up to 16MB</p>
                      </div>
                    </button>

                    <div className="my-1 border-t border-slate-100 dark:border-slate-700/50" />

                    <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      1-Click Ready Documents
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAttachPreset("proposal")}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors cursor-pointer text-left"
                    >
                      <FileText className="w-4 h-4 text-rose-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate">2-Page Proposal & SOW (PDF)</p>
                        <p className="text-[10px] font-normal text-slate-400">Official estimate & deliverables</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleAttachPreset("invoice")}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors cursor-pointer text-left"
                    >
                      <FileCheck className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate">Proforma Tax Invoice (PDF)</p>
                        <p className="text-[10px] font-normal text-slate-400">Includes Bank & UPI details</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleAttachPreset("brochure")}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors cursor-pointer text-left"
                    >
                      <FileText className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate">Real Estate CRM Brochure (PDF)</p>
                        <p className="text-[10px] font-normal text-slate-400">Features, ROI & specs</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}

            <textarea
              ref={textareaRef}
              rows={2}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={channel === "whatsapp" ? (pendingAttachment ? "Add a message with this attachment... (Enter to send)" : "Type a WhatsApp reply... (Enter to send)") : "Type an SMS text to buyer phone... (Enter to send)"}
              className={`flex-1 resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-3 text-xs text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                channel === "whatsapp" ? "focus:border-emerald-500 focus:ring-emerald-500" : "focus:border-blue-500 focus:ring-blue-500"
              }`}
            />

            <button
              onClick={() => handleSendMessage()}
              disabled={sending || (!inputMessage.trim() && !pendingAttachment)}
              className={`h-10 px-4 rounded-xl disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md transition-all shrink-0 cursor-pointer ${
                channel === "whatsapp" 
                  ? "bg-emerald-600 hover:bg-emerald-500 hover:shadow-emerald-500/20" 
                  : "bg-blue-600 hover:bg-blue-500 hover:shadow-blue-500/20"
              }`}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>

          <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1.5 px-1">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-cyan-400" />
              {channel === "whatsapp" ? "Official Meta Cloud API • Document & Media Enabled" : "Twilio US 10DLC Gateway • Instant SMS Speed-to-Lead"}
            </span>
            <span>Press <strong>Enter</strong> to send</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Alias for universal usage
export const MultiChannelChatDrawer = WhatsAppChatDrawer;
